"use client";

import React from "react";
import { ArrowRight, CircleAlert, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function InsightsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="131149" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i}>
            <Card className="flex h-full flex-col overflow-hidden rounded-xl border-border/40 bg-card shadow-sm">
              <AspectRatio ratio={16 / 9} className="bg-muted/50 animate-pulse" />
              <CardContent className="flex flex-grow flex-col p-6">
                <div className="mb-4 h-5 w-24 animate-pulse rounded-full bg-muted/50" />
                <div className="mb-2 h-7 w-full animate-pulse rounded-md bg-muted/50" />
                <div className="mb-6 h-7 w-2/3 animate-pulse rounded-md bg-muted/50" />
                <div className="mt-auto h-4 w-32 animate-pulse rounded-md bg-muted/50" />
              </CardContent>
            </Card>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border/50 bg-muted/20 py-16 text-center">
        <CircleAlert className="mb-4 size-8 text-muted-foreground" />
        <p className="mb-4 text-muted-foreground">Failed to load latest insights.</p>
        <Button variant="outline" onClick={refetch} className="rounded-full border-primary text-primary hover:bg-primary/5">
          Try Again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border/50 bg-muted/20 py-16 text-center">
        <FileText className="mb-4 size-8 text-muted-foreground" />
        <p className="text-muted-foreground font-sans">No insights published yet.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <div key={post.id} data-index={i}>
          <Card className="flex h-full flex-col overflow-hidden rounded-xl border-border/40 bg-card shadow-sm transition-all duration-300 hover:shadow-lg">
            <Link to={post.link || "#"} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <AspectRatio ratio={16 / 9} className="bg-muted overflow-hidden">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-secondary/20">
                    <FileText className="size-10 text-muted-foreground/30" />
                  </div>}
              </AspectRatio>
            </Link>
            <CardContent className="flex flex-grow flex-col p-6">
              <div className="mb-4">
                {post.categories && post.categories.length > 0 ? <Badge variant="secondary" className="rounded-full bg-secondary/60 px-3 py-1 text-[12px] font-sans font-semibold uppercase tracking-wider text-secondary-foreground">
                    {post.categories[0].name}
                  </Badge> : <Badge variant="secondary" className="rounded-full bg-secondary/60 px-3 py-1 text-[12px] font-sans font-semibold uppercase tracking-wider text-secondary-foreground">
                    Article
                  </Badge>}
              </div>
              <h3 className="mb-6 font-serif text-xl font-bold leading-snug text-foreground md:text-2xl">
                <Link to={post.link || "#"} className="hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                  {post.title}
                </Link>
              </h3>
              <div className="mt-auto pt-4 border-t border-border/20">
                <Link to={post.link || "#"} className="group inline-flex items-center text-sm font-semibold font-sans text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm transition-all">
                  Read full article <ArrowRight className="ml-1.5 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>)}
    </div>;
}
export default function LatestInsights() {
  const wp_query_params = {
    per_page: 6,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-insights-and-articles" className="bg-background py-20 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 md:mb-16 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
              Latest Insights & Articles
            </h2>
            <p className="mt-4 text-lg font-sans leading-relaxed text-muted-foreground">
              Expert articles and lived-experience insights for parents and educators supporting children with PDA in Australia.
            </p>
          </div>
          <Button variant="outline" asChild className="shrink-0 rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300">
            <Link to="/ArticlesPage">
              View All Articles
            </Link>
          </Button>
        </div>

        <PostsProvider wp_query={{
        per_page: 6,
        orderby: "date",
        order: "desc"
      }}>
          <InsightsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}